import { useState } from "react";
import { api } from "../../api";
import { Empty, ErrorNote, formatBytes, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, Field, Input } from "../../components/Shared";
import { useApi } from "../../useApi";
import { SuccessNote } from "./parts";

// Management -> Usage and quotas (task 2026-10-05-project-databases-and-storage-are-measured-
// and-capped). GET /api/admin/usage: every project's database size and S3 bytes with its quota
// state; GET /api/admin/usage/{id}: rows per table, imports per day, slowest queries;
// GET/PUT /api/admin/quotas {db_mib, s3_mib, warn_percent} (0 = no quota, per project).

const STATE = {
  none: { color: "gray", label: "no quota" },
  ok: { color: "green", label: "ok" },
  warn: { color: "amber", label: "warning" },
  full: { color: "red", label: "full" },
};

export function QuotaState({ s }) {
  const st = STATE[s.state] || STATE.none;
  return (
    <span>
      {formatBytes(s.used)}
      {s.quota ? ` of ${formatBytes(s.quota)} (${s.percent} %)` : ""}{" "}
      <Badge color={st.color}>{st.label}</Badge>
    </span>
  );
}

const TH = { padding: "8px 12px", textAlign: "left" };
const TD = { padding: "8px 12px", borderTop: "1px solid #f3f4f6", verticalAlign: "top" };
const HEAD = { color: "#6b7280", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" };

function QuotaForm({ quotas, onSaved }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const v = form || quotas;
  const set = (k, value) => setForm({ ...v, [k]: value });

  const save = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const out = await api.put("/admin/quotas", {
        db_mib: Number(v.db_mib) || 0, s3_mib: Number(v.s3_mib) || 0, warn_percent: Number(v.warn_percent) || 80 });
      setForm(out);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save}>
      <ErrorNote error={error} />
      {saved && <SuccessNote>Saved: the quotas apply to the next upload or import.</SuccessNote>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        <Field label="Database quota per project (MiB)" hint="0 = no quota. At 100 % imports are refused.">
          <Input type="number" min={0} value={v.db_mib} onChange={e => set("db_mib", e.target.value)} />
        </Field>
        <Field label="Storage quota per project (MiB)" hint="0 = no quota. At 100 % uploads are refused.">
          <Input type="number" min={0} value={v.s3_mib} onChange={e => set("s3_mib", e.target.value)} />
        </Field>
        <Field label="Warn from (%)" hint="Management and the Files tab warn from this share.">
          <Input type="number" min={1} max={100} value={v.warn_percent} onChange={e => set("warn_percent", e.target.value)} />
        </Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <Btn type="submit" disabled={busy}>Save quotas</Btn>
      </div>
    </form>
  );
}

// What the storage of a project is made of (usage.s3_parts); the live uploads come from the
// hourly measurement `measure-usage`, so they carry its time.
const PARTS = [
  ["files", "File versions"],
  ["imports", "Table imports"],
  ["releases", "Release files"],
  ["live", "Files uploaded into the hosted database"],
];

function StorageParts({ parts }) {
  if (!parts) return null;
  return (
    <>
      <h3 style={{ fontSize: 15, margin: "6px 0" }}>Storage by kind</h3>
      <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 13 }}>
        {PARTS.map(([k, label]) => parts[k] && (
          <li key={k}>
            {label}: {formatBytes(parts[k].bytes)}, {parts[k].objects} objects
            {k === "live" && (parts.live.measured_at
              ? ` (measured ${new Date(parts.live.measured_at).toLocaleString()})`
              : " (not measured yet)")}
          </li>
        ))}
      </ul>
    </>
  );
}

function ProjectDetail({ id }) {
  const r = useApi(`/admin/usage/${id}`);
  if (r.loading) return <Loading />;
  if (r.error) return <ErrorNote error={r.error} />;
  const d = r.data;
  const sq = d.slowest_queries;
  return (
    <div style={{ padding: "8px 0 4px" }}>
      <StorageParts parts={d.s3 && d.s3.parts} />
      <h3 style={{ fontSize: 15, margin: "6px 0" }}>Rows per table</h3>
      {d.tables.length === 0 ? <Empty>No database built yet.</Empty> : (
        <table className="responsive-table" style={{ borderCollapse: "collapse", fontSize: 13 }}>
          <caption style={{ position: "absolute", left: -9999 }}>Rows per table of {d.database.name}</caption>
          <thead><tr style={HEAD}><th scope="col" style={TH}>Table</th><th scope="col" style={TH}>Rows</th></tr></thead>
          <tbody>
            {d.tables.map(t => (
              <tr key={`${t.schema}.${t.table}`}>
                <td data-label="Table" style={TD}>{t.schema === "public" ? t.table : `${t.schema}.${t.table}`}</td>
                <td data-label="Rows" style={TD}>{t.estimated ? `about ${t.rows}` : t.rows}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 style={{ fontSize: 15, margin: "14px 0 6px" }}>Imports per day (30 days)</h3>
      {d.imports_per_day.length === 0 ? <Empty>No imports in the last 30 days.</Empty> : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          {d.imports_per_day.map(i => (
            <li key={i.day}>{i.day}: {i.uploaded} uploaded, {i.committed} committed</li>
          ))}
        </ul>
      )}
      <h3 style={{ fontSize: 15, margin: "14px 0 6px" }}>Slowest queries</h3>
      {!sq.available ? <p style={{ fontSize: 13, color: "#4b5563", margin: 0 }}>Not available: {sq.reason}</p>
        : sq.queries.length === 0 ? <Empty>No queries recorded.</Empty> : (
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {sq.queries.map((q, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                {q.mean_ms} ms on average, {q.calls} calls
                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: 12, margin: "2px 0 0" }}>{q.query}</pre>
              </li>
            ))}
          </ol>
        )}
    </div>
  );
}

export default function UsageCard() {
  const r = useApi("/admin/usage");
  const [open, setOpen] = useState(null);

  if (r.loading && !r.data) return <Loading />;
  if (r.error) return <ErrorNote error={r.error} />;
  const { projects, quotas, database_error: dbError } = r.data;
  return (
    <>
      <Card>
        <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Usage</h2>
        {dbError && <p role="alert" style={{ fontSize: 13, color: "#991b1b" }}>Database sizes could not be read: {dbError}</p>}
        {projects.length === 0 ? <Empty>No projects yet.</Empty> : (
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <caption style={{ position: "absolute", left: -9999 }}>Usage per project</caption>
              <thead>
                <tr style={HEAD}>
                  <th scope="col" style={TH}>Project</th>
                  <th scope="col" style={TH}>Database</th>
                  <th scope="col" style={TH}>Storage (S3)</th>
                  <th scope="col" style={TH}><span style={{ position: "absolute", left: -9999 }}>Details</span></th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id}>
                    <td data-label="Project" style={TD}>
                      {p.name} <span style={{ color: "#6b7280", fontSize: 12 }}>({p.slug})</span>
                      {open === p.id && <ProjectDetail id={p.id} />}
                    </td>
                    <td data-label="Database" style={TD}>{p.database.exists ? <QuotaState s={p.database} /> : <span style={{ color: "#6b7280" }}>not built</span>}</td>
                    <td data-label="Storage (S3)" style={TD}><QuotaState s={p.s3} /> <span style={{ fontSize: 12, color: "#6b7280" }}>{p.s3.objects} objects</span></td>
                    <td style={{ ...TD, textAlign: "right" }}>
                      <Btn variant="secondary" onClick={() => setOpen(open === p.id ? null : p.id)}>
                        {open === p.id ? "Hide details" : "Details"}<span style={{ position: "absolute", left: -9999 }}> of {p.name}</span>
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ fontSize: 13, color: "#4b5563", margin: "14px 0 0" }}>
          Storage counts every file version whose bytes are kept (also replaced and deleted files,
          until an administrator purges them), the files of table imports, release files and the
          files uploaded into a hosted database. The last are measured once an
          hour, so a project can pass its quota by what it uploads within that hour.
        </p>
      </Card>
      <div style={{ height: 16 }} />
      <Card>
        <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Quotas</h2>
        <QuotaForm quotas={quotas} onSaved={r.reload} />
      </Card>
    </>
  );
}
